import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, orderBy, getCountFromServer, where, startAt, endAt, limit } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { Santri, NewSantriData } from '../types';

export interface SantriStats {
    nonaktif: { total: number; laki: number; perempuan: number };
    alumni: { total: number; laki: number; perempuan: number; paud: number; sd: number; smp: number; sma: number };
}

export interface SantriContextType {
    loading: boolean;
    santri: Santri[];
    santriStats: SantriStats;

    addSantri: (santriData: NewSantriData) => Promise<any>;
    updateSantri: (santriId: string, santriData: Partial<NewSantriData>) => Promise<void>;
    deleteSantri: (santriId: string) => Promise<void>;
    addMultipleSantri: (santriData: NewSantriData[]) => Promise<void>;
    deleteMultipleSantri: (santriIds: string[]) => Promise<void>;
    updateMultipleSantri: (updates: { santriId: string; data: Partial<NewSantriData> }[]) => Promise<void>;
    searchSantri: (keyword: string) => Promise<Santri[]>;
    fetchSantriCustomClasses: (targetClasses: string[] | null) => Promise<void>;
}

export const SantriContext = createContext<SantriContextType | undefined>(undefined);

export const useSantri = () => {
    const context = useContext(SantriContext);
    if (context === undefined) {
        throw new Error('useSantri must be used within a SantriProvider');
    }
    return context;
};

export const SantriProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();

    const [santri, setSantri] = useState<Santri[]>([]);
    const [santriStats, setSantriStats] = useState<SantriStats>({
        nonaktif: { total: 0, laki: 0, perempuan: 0 },
        alumni: { total: 0, laki: 0, perempuan: 0, paud: 0, sd: 0, smp: 0, sma: 0 }
    });
    const [loading, setLoading] = useState(true);

    const [hasFetchedGlobalSantri, setHasFetchedGlobalSantri] = useState(false);

    const fetchSantriCustomClasses = async (targetClasses: string[] | null = null) => {
        try {
            let santriData: Santri[] = [];

            if (targetClasses === null) {
                // Return immediately if we already fetched global active students to prevent unnecessary reads
                if (hasFetchedGlobalSantri && santri.length > 0) return;

                const santriQuery = query(collection(db, 'santri'), where('status', '==', 'Aktif'));
                const snapshot = await getDocs(santriQuery);
                santriData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Santri));
            } else if (targetClasses.length > 0) {
                const chunks = [];
                for (let i = 0; i < targetClasses.length; i += 10) {
                    chunks.push(targetClasses.slice(i, i + 10));
                }

                const promises = chunks.map(chunk => {
                    const chunkQuery = query(
                        collection(db, 'santri'),
                        where('status', '==', 'Aktif'),
                        where('kelas', 'in', chunk)
                    );
                    return getDocs(chunkQuery);
                });

                const snapshots = await Promise.all(promises);
                snapshots.forEach(snap => {
                    santriData = [...santriData, ...snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Santri))];
                });
            }

            santriData.sort((a, b) => (a.nama || '').localeCompare(b.nama || ''));

            // Only overwrite state if we are fetching global (null) to avoid losing subset classes data,
            // or if it's a specific class request, we append it if not exist.
            // However, the current logic replaces the entire state.
            // For now we keep replacing but track global fetch status.
            if (targetClasses === null) setHasFetchedGlobalSantri(true);
            setSantri(santriData);
        } catch (e) {
            console.error("Error fetching santri", e);
        }
    };

    const fetchStats = async () => {
        try {
            const coll = collection(db, 'santri');
            const qNonL = query(coll, where('status', '==', 'Nonaktif'), where('jenisKelamin', '==', 'Laki-laki'));
            const qNonP = query(coll, where('status', '==', 'Nonaktif'), where('jenisKelamin', '==', 'Perempuan'));

            const alumniStatuses = ['Alumni', 'Alumni PAUD', 'Alumni SD', 'Alumni SMP', 'Alumni SMA'];
            const qAluL = query(coll, where('status', 'in', alumniStatuses), where('jenisKelamin', '==', 'Laki-laki'));
            const qAluP = query(coll, where('status', 'in', alumniStatuses), where('jenisKelamin', '==', 'Perempuan'));

            const qPaud = query(coll, where('status', '==', 'Alumni PAUD'));
            const qSd = query(coll, where('status', '==', 'Alumni SD'));
            const qSmp = query(coll, where('status', '==', 'Alumni SMP'));
            const qSma = query(coll, where('status', '==', 'Alumni SMA'));

            const [snL, snP, saL, saP, sPaud, sSd, sSmp, sSma] = await Promise.all([
                getCountFromServer(qNonL), getCountFromServer(qNonP),
                getCountFromServer(qAluL), getCountFromServer(qAluP),
                getCountFromServer(qPaud), getCountFromServer(qSd),
                getCountFromServer(qSmp), getCountFromServer(qSma)
            ]);

            setSantriStats({
                nonaktif: { total: snL.data().count + snP.data().count, laki: snL.data().count, perempuan: snP.data().count },
                alumni: {
                    total: saL.data().count + saP.data().count,
                    laki: saL.data().count,
                    perempuan: saP.data().count,
                    paud: sPaud.data().count,
                    sd: sSd.data().count,
                    smp: sSmp.data().count,
                    sma: sSma.data().count
                }
            });
        } catch (e) {
            console.error("Stats fetch error", e);
        }
    };

    useEffect(() => {
        if (!authUser) {
            setSantri([]);
            setHasFetchedGlobalSantri(false);
            setLoading(false);
            return;
        }

        // We fetch the stats globally, but the individual `fetchSantriCustomClasses`
        // will be triggered by PermitContext / application logic depending on access levels.
        fetchStats().then(() => setLoading(false));

        // Warning: This replicates the old logic. Initially it doesn't fetch `santri` directly here
        // because `PermitContext` handled access roles to determine which classes to fetch.
        // We will expose `fetchSantriCustomClasses` to allow `PermitContext` (or other managers)
        // to invoke the fetch once they determine the needed classes.

    }, [authUser]);

    const addSantri = async (santriData: NewSantriData) => {
        const docRef = await addDoc(collection(db, 'santri'), santriData);
        if (santriData.status === 'Aktif') {
            setSantri(prev => {
                const newSantri = { id: docRef.id, ...santriData } as Santri;
                return [...prev, newSantri].sort((a, b) => (a.nama || '').localeCompare(b.nama || ''));
            });
        }
        return docRef;
    };
    const updateSantri = async (santriId: string, santriData: Partial<NewSantriData>) => {
        await updateDoc(doc(db, 'santri', santriId), santriData);
        setSantri(prev => prev.map(s => s.id === santriId ? { ...s, ...santriData } as Santri : s));
    };
    const deleteSantri = async (santriId: string) => {
        await deleteDoc(doc(db, 'santri', santriId));
        setSantri(prev => prev.filter(s => s.id !== santriId));
    };

    const deleteMultipleSantri = async (santriIds: string[]) => {
        if (santriIds.length === 0) return;
        const batch = writeBatch(db);
        santriIds.forEach(id => batch.delete(doc(db, 'santri', id)));
        await batch.commit();
        setSantri(prev => prev.filter(s => !santriIds.includes(s.id)));
    };

    const addMultipleSantri = async (santriData: NewSantriData[]) => {
        const batch = writeBatch(db);
        santriData.forEach(data => batch.set(doc(collection(db, 'santri')), data));
        await batch.commit();
    };

    const updateMultipleSantri = async (updates: { santriId: string; data: Partial<NewSantriData> }[]) => {
        if (updates.length === 0) return;
        const batch = writeBatch(db);
        updates.forEach(update => batch.update(doc(db, 'santri', update.santriId), update.data));
        await batch.commit();
    };

    const searchSantri = async (keyword: string): Promise<Santri[]> => {
        if (!keyword || keyword.length < 3) return [];

        const keywords = new Set<string>();
        keywords.add(keyword);
        keywords.add(keyword.charAt(0).toUpperCase() + keyword.slice(1).toLowerCase());
        keywords.add(keyword.toUpperCase());

        const queries = Array.from(keywords).map(k =>
            getDocs(query(
                collection(db, 'santri'),
                orderBy('nama'),
                startAt(k),
                endAt(k + '\uf8ff'),
                limit(20)
            ))
        );

        const snapshots = await Promise.all(queries);

        const seen = new Set<string>();
        const merged: Santri[] = [];
        snapshots.forEach(snap => {
            snap.docs.forEach(doc => {
                if (!seen.has(doc.id)) {
                    seen.add(doc.id);
                    merged.push({ id: doc.id, ...doc.data() } as Santri);
                }
            });
        });

        return merged;
    };

    return (
        <SantriContext.Provider value={{
            loading,
            santri, santriStats,
            addSantri, updateSantri, deleteSantri, addMultipleSantri, deleteMultipleSantri, updateMultipleSantri,
            searchSantri, fetchSantriCustomClasses
        }}>
            {children}
        </SantriContext.Provider>
    );
};
